import React, { useState, useEffect, useMemo, useRef } from "react";
import "./TableViews.scss";

// Helper: Convert integer to IPv4 string
function intToIPv4(int) {
  if (!int) return "";
  const num = Number(int);
  if (isNaN(num)) return int;
  return [
    (num >>> 24) & 255,
    (num >>> 16) & 255,
    (num >>> 8) & 255,
    num & 255,
  ].join(".");
}

// Helper: Convert timestamp (ms) to readable date
function formatTimestamp(ts) {
  let num = Number(ts);
  if (isNaN(num)) return ts;
  if (num < 1000000000000) num = num * 1000;
  const d = new Date(num);
  if (isNaN(d.getTime())) return ts;
  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "medium",
    });
    return formatter.format(d);
  } catch {
    return d.toLocaleString();
  }
}

// Helper: Format duration in ms to human-friendly string
function formatDurationMs(ms) {
  const value = Number(ms) || 0;
  if (value >= 1000) {
    const seconds = value / 1000;
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remSec = Math.round(seconds % 60);
      return `${minutes}m ${remSec}s`;
    }
    return `${seconds.toFixed(2)}s`;
  }
  return `${Math.round(value)}ms`;
}

// Helper: Parse a numeric value from mixed input
function parseNumericValue(input) {
  if (typeof input === "number") return input;
  if (typeof input === "string") {
    const match = input.match(/-?[\d.]+/);
    if (match) return parseFloat(match[0]);
  }
  return NaN;
}

// Normalize microseconds value
function normalizeLatencyMicrosecondsToSeconds(rawValue) {
  const num = parseNumericValue(rawValue);
  if (isNaN(num)) return null;
  const microseconds = Math.abs(num);
  let seconds = microseconds / 1_000_000;
  if (seconds > 1) seconds = 1;
  return seconds;
}

// Map chart category/name to an icon
function getChartIconForCategory(category, name) {
  const key = (category || name || "").toString().toLowerCase();
  if (key.includes("bar")) return "📊";
  if (key.includes("line")) return "📈";
  if (key.includes("area")) return "📈";
  if (key.includes("scatter")) return "🔹";
  if (key.includes("pie") || key.includes("donut")) return "🥧";
  if (key.includes("histogram")) return "📊";
  if (key.includes("heatmap")) return "🔥";
  if (key.includes("table")) return "📋";
  if (key.includes("map")) return "🗺️";
  return "📊";
}

function TableViews({ data, onSearchChange, suggestedCharts = [], onChartSelect = () => { }, isHistoryOpen = false, datasetKey = "" }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterConfig, setFilterConfig] = useState({
    showFilters: false,
    filters: {}
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 15 : 20); // dynamic rows per page based on available height

  // Mobile Column Selection State
  const [mobileActiveCols, setMobileActiveCols] = useState([]); // Live table columns
  const [mobileVisibleColumns, setMobileVisibleColumns] = useState([]); // Temporary picker state
  const [isHeaderDropdownOpen, setIsHeaderDropdownOpen] = useState(false);

  const MAX_MB = 100;

  const containersRef = useRef(null);
  const tableRef = useRef(null);
  const tableMainRef = useRef(null);
  const expandBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const ROWS_MIN = 3;
  const ROWS_MAX = 500;

  // Calculate data size
  const getDataSizeMB = (arr) => {
    try {
      const jsonStr = JSON.stringify(arr);
      const bytes = new TextEncoder().encode(jsonStr).length;
      return bytes / (1024 * 1024);
    } catch {
      return 0;
    }
  };
  const dataSizeMB = useMemo(() => getDataSizeMB(data), [data]);

  // Warn if data too big
  useEffect(() => {
    if (data.length > 0 && dataSizeMB > MAX_MB) {
      alert("Data CSV is more than 100MB, download to see full data");
    }
  }, [data, dataSizeMB]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && filterConfig.showFilters) {
        handleFilterToggle();
      }
      if (e.key === "Escape" && isHeaderDropdownOpen) {
        setIsHeaderDropdownOpen(false);
      }
    };
    const onClickOutside = (e) => {
      if (isHeaderDropdownOpen && !e.target.closest('.column-picker-wrapper')) {
        setIsHeaderDropdownOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("click", onClickOutside);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("click", onClickOutside);
    };
  }, [filterConfig.showFilters, isHeaderDropdownOpen]);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // Initialize mobile columns
  useEffect(() => {
    if (isMobile && data.length > 0) {
      const allCols = Object.keys(data[0]);
      // If we already have explicit active columns from a previous selection, don't reset
      if (mobileActiveCols.length === 0) {
        const defaults = [];
        // Priority defaults
        if (allCols.includes("connected_from")) defaults.push("connected_from");
        if (allCols.includes("operation")) defaults.push("operation");

        // Fill up to 2
        allCols.forEach(c => {
          if (defaults.length < 2 && !defaults.includes(c)) defaults.push(c);
        });
        setMobileActiveCols(defaults);
      }
    }
  }, [data, isMobile, mobileActiveCols.length]);

  const toggleColumnSelection = (col) => {
    setMobileVisibleColumns(prev => {
      let next = [];
      if (prev.includes(col)) {
        next = prev.filter(c => c !== col);
      } else {
        if (prev.length >= 2) return prev;
        next = [...prev, col];
      }

      // If we've reached 2 columns, apply to table and reset picker
      if (next.length === 2 && !prev.includes(col)) {
        setMobileActiveCols(next);
        setIsHeaderDropdownOpen(false); // Auto-close on selection
        return []; // Clear picker (uncheck all)
      }
      return next;
    });
  };

  // Notify parent when search changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm, filterConfig.filters);
    }
  }, [searchTerm, filterConfig.filters, onSearchChange]);

  // Filter and sort data
  let filteredData = data;

  // Apply search filter
  if (searchTerm && searchTerm.trim() !== "") {
    filteredData = filteredData.filter((row) => {
      return Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase().trim())
      );
    });
  }

  // Apply column filters
  if (filterConfig.filters && Object.keys(filterConfig.filters).length > 0) {
    filteredData = filteredData.filter((row) => {
      return Object.entries(filterConfig.filters).every(([column, filterValue]) => {
        if (!filterValue || filterValue.trim() === "") return true;
        const cellValue = row[column]?.toString().toLowerCase() || "";
        return cellValue.includes(filterValue.toLowerCase().trim());
      });
    });
  }

  // Apply sorting
  if (sortConfig.key) {
    filteredData = [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  // Slice data if too large
  let displayData = filteredData;
  if (dataSizeMB > MAX_MB) {
    const avgRowSize = dataSizeMB / data.length;
    const maxRows = Math.floor(MAX_MB / avgRowSize);
    displayData = filteredData.slice(0, maxRows);
  }

  const allColumns = data.length > 0 ? Object.keys(data[0]) : [];
  const columns = isMobile && mobileActiveCols.length === 2 ? mobileActiveCols : allColumns;
  const totalPages = Math.ceil(displayData.length / rowsPerPage);

  const getVisiblePageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages]);
    for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
      if (page > 1 && page < totalPages) {
        pages.add(page);
      }
    }

    if (currentPage <= 3) {
      pages.add(2);
      pages.add(3);
      pages.add(4);
    }

    if (currentPage >= totalPages - 2) {
      pages.add(totalPages - 1);
      pages.add(totalPages - 2);
      pages.add(totalPages - 3);
    }

    return Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((left, right) => left - right);
  };

  const visiblePageNumbers = getVisiblePageNumbers();

  const paginationItems = visiblePageNumbers.flatMap((page, index) => {
    const previousPage = visiblePageNumbers[index - 1];
    if (index === 0) return [page];
    if (page - previousPage === 1) return [page];
    return ["ellipsis-" + previousPage + "-" + page, page];
  });

  // Adjust rowsPerPage dynamically based on available container height and row height
  useEffect(() => {
    const computeRows = () => {
      const container = containersRef.current || document.querySelector(".tableContainers");
      const table = tableRef.current || document.querySelector(".table-cont");
      if (!container || !table) return;

      // Viewport-based: measure actual fixed elements instead of capped container height
      const viewH = window.innerHeight;
      const navEl = document.querySelector('.HomepageWrapperHead');
      const navH = navEl ? navEl.offsetHeight : 56;
      const theadEl = table.querySelector('thead');
      const theadH = theadEl ? theadEl.offsetHeight : 38;
      const paginationH = isMobile ? 36 : 40; // fixed pagination bar
      const footerH = isMobile ? 56 : 80;     // fixed footer search bar
      const extraLayoutPadding = isMobile ? 20 : 120; // tabs, container padding, wrapper padding-bottom
      const margin = 16;
      const reserved = navH + theadH + paginationH + footerH + extraLayoutPadding + margin;
      const availableHeight = Math.max(viewH - reserved, 80);
      const firstRow = table.querySelector('tbody tr.data-row');
      const rowHeight = firstRow ? firstRow.getBoundingClientRect().height : 36;

      const calc = Math.max(isMobile ? 12 : ROWS_MIN, Math.min(ROWS_MAX, Math.floor(availableHeight / rowHeight)));
      setRowsPerPage(calc);
    };

    computeRows();

    const ro = new ResizeObserver(computeRows);
    const containerNode = containersRef.current || document.querySelector(".tableContainers");
    if (containerNode) ro.observe(containerNode);
    ro.observe(document.body);
    window.addEventListener("resize", computeRows);

    // watch for content changes (data length)
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", computeRows);
    };
  }, [displayData.length, data, isMobile]);

  // Calculate uniform column width (percentage string)
  const getUniformColumnWidth = () => {
    if (isMobile) return "50%";
    const cols = Math.max(1, columns.length);
    // Use floor so total doesn't exceed 100% due to rounding
    const percent = Math.floor(100 / cols);
    return `${percent}%`;
  };

  const uniformColumnWidth = getUniformColumnWidth();

  // Page input sync
  useEffect(() => {
    setPageInput(currentPage);
  }, [currentPage]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = displayData.slice(indexOfFirstRow, indexOfLastRow);

  // ensure currentPage stays within range when rowsPerPage or data changes
  useEffect(() => {
    const tp = Math.ceil(displayData.length / rowsPerPage) || 1;
    if (currentPage > tp) {
      setCurrentPage(tp);
    }
  }, [rowsPerPage, displayData.length]);

  useEffect(() => {
    setCurrentPage(1);
  }, [datasetKey]);

  const handlePageChange = (value) => {
    const page = parseInt(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    } else {
      setPageInput(currentPage);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterToggle = () => {
    setFilterConfig((prev) => {
      console.log("Toggling filters from handler", prev.showFilters, filterConfig);
      return {
        ...prev,
        showFilters: !prev.showFilters
      };
    });
  };

  const handleColumnFilter = (column, value) => {
    setFilterConfig((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        [column]: value
      }
    }));
  };

  const clearAllFilters = () => {
    setFilterConfig((prev) => ({
      ...prev,
      filters: {}
    }));
    setSearchTerm("");
  };

  const toggleRowSelection = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const toggleAllRows = () => {
    if (selectedRows.length === currentRows.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(currentRows.map((_, idx) => indexOfFirstRow + idx));
    }
  };

  const getLatencyColor = (col, value) => {
    if (col.toLowerCase() === "latency" || col.toLowerCase() === "response_time") {
      const secs = normalizeLatencyMicrosecondsToSeconds(value);
      if (secs === null) return "";
      if (secs < 0.5) return "latency-fast";
      if (secs < 1) return "latency-medium";
      return "latency-slow";
    }
    return "";
  };

  // CSV download
  const downloadCSV = () => {
    const columnsFull = data.length > 0 ? Object.keys(data[0]) : [];
    const csvRows = [columnsFull.join(",")];

    data.forEach((row) => {
      const values = columnsFull.map((col) => {
        let val = row[col];
        if (col === "connected_from") {
          val = intToIPv4(val);
        } else if (col.toLowerCase().includes("timestamp")) {
          val = formatTimestamp(val);
        } else if (col === "subject") {
          val = getSubjectDisplay(val, row);
        }
        return `"${(val || "").toString().replace(/"/g, "\"\"")}"`;
      });
      csvRows.push(values.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table-data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Listen for header download requests (CSV)
  useEffect(() => {
    const onDownloadCharts = (e) => {
      const { format } = (e && e.detail) ? e.detail : { format: "csv" };
      if (format === "csv") {
        downloadCSV();
      }
    };

    document.addEventListener("downloadCharts", onDownloadCharts);
    return () => document.removeEventListener("downloadCharts", onDownloadCharts);
  }, [data]);

  // Shadow effect on scroll (use ref)
  useEffect(() => {
    const container = containersRef.current || document.querySelector(".tableContainers");
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollTop > 0);
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [containersRef.current]);

  useEffect(() => {
    const onToggleFilters = () => {
      console.log("Toggling filters from event");
      handleFilterToggle();
    };

    document.addEventListener("toggleFilters", onToggleFilters);

    return () => {
      document.removeEventListener("toggleFilters", onToggleFilters);
    };
  }, [handleFilterToggle]);

  // Get display value for subject based on operation
  const getSubjectDisplay = (subject, rowContext) => {
    if (!subject || typeof subject !== 'object') return subject;

    const operation = (rowContext && (
      rowContext.operation ||
      rowContext.Operation ||
      (rowContext.details && rowContext.details.Operation) ||
      (rowContext.DETAILS && rowContext.DETAILS.Operation)
    )) || '';

    let path = '';
    switch (operation) {
      case 'GetChunk':
      case 'StartWrite':
      case 'FinalizeWrite':
      case 'UpdateTags':
      case 'AddComment':
      case 'RemoveComment':
      case 'UpdateComment':
      case 'RemoveVersion':
      case 'GetComment':
        path = subject.path_of_object;
        break;

      case 'CopyFile':
      case 'MoveFile':
      case 'RenameFile':
      case 'MoveFolder':
      case 'RenameFolder':
        path = subject.dest_path_in_subject || 'N/A';
        break;

      case 'CreateFolder':
        return subject.name_of_new_folder || 'N/A';

      case 'Share':
      case 'UpdateShare':
        path = subject.acl?.object_path;
        break;

      case 'UpdateRole':
      case 'AddRole':
      case 'RemoveRole':
        return subject.role_name || 'N/A';

      case 'AddTeam':
      case 'UpdateTeam':
      case 'RemoveTeam':
        return subject.team_name || 'N/A';

      case 'SetRolesTOUser':
        return subject.email || 'N/A';

      case 'ResetVersion':
        return subject.file_name || 'N/A';

      case 'ValidateCoupon':
        return subject.coupon || 'N/A';

      case 'RemoveOrganizationUser':
        return subject.email || 'N/A';

      case 'UntrashFile':
        return subject.subject || 'N/A';

      case 'UntrashFolder':
        return subject.subject || 'N/A';

      case 'RemoveFolder':
        return subject.subject || 'N/A';
      case 'RemoveFile':
        return subject.subject || 'N/A';

      default:
        // Return raw subject if we don't know the operation, formatting handled by caller
        return subject;
    }

    if (path) {
      // Extract the name (basename) from the path
      return path;
    }
    return 'N/A';
  };

  // Extract location display from a location object
  const getLocationDisplay = (location) => {
    if (!location || typeof location !== 'object') return location;
    const parts = [];
    if (location.region) parts.push(location.region);
    if (location.country) parts.push(location.country);
    if (parts.length > 0) return parts.join(', ');
    // Fallback: try common alternative keys
    if (location.city) parts.push(location.city);
    if (location.country_name) parts.push(location.country_name);
    if (parts.length > 0) return parts.join(', ');
    return JSON.stringify(location);
  };

  // Format object values for table cell display
  const formatObjectForCell = (col, value) => {
    if (value == null || typeof value !== 'object') return value;
    const colLower = col.toLowerCase();
    if (colLower === 'location' || colLower.includes('location')) {
      return getLocationDisplay(value);
    }
    // For other object columns, show stringified in cell
    return JSON.stringify(value);
  };

  // Render cell value
  const renderCell = (col, value, row) => {
    const colLower = col.toLowerCase();
    let displayValue = value;

    if (colLower === "details") {
      displayValue = value;
    } else if (colLower === "connected_from") {
      displayValue = intToIPv4(value);
    } else if (colLower.includes("timestamp")) {
      displayValue = formatTimestamp(value);
    } else if (colLower === "subject") {
      displayValue = getSubjectDisplay(value, row);
    }

    if (displayValue != null && typeof displayValue === 'object') {
      displayValue = formatObjectForCell(col, displayValue);
    }

    const strValue = displayValue != null ? displayValue.toString() : "";

    // Message/Status badge
    if (colLower === "message" || colLower === "status") {
      const lowerVal = strValue.toLowerCase();
      const isSuccess = lowerVal.includes("success") || lowerVal === "true" || value === true || (typeof value === 'number' && value >= 200 && value < 300);
      const isError = lowerVal.includes("error") || lowerVal.includes("fail") || lowerVal === "false" || value === false || (typeof value === 'number' && (value >= 400 || value < 0));

      return (
        <span className={`message-badge ${isSuccess ? "success" : "error"}`}>
          {isSuccess ? "✓" : "✗"}
        </span>
      );
    }

    // Latency
    if (colLower === "latency" || colLower === "response_time") {
      const secs = normalizeLatencyMicrosecondsToSeconds(value);
      if (secs == null) {
        return <span className="cell-text">{strValue}</span>;
      }
      return <span className="cell-text">{secs.toFixed(3)}s</span>;
    }

    if (colLower === "details") {
      return (
        <span className="details-data-badge">
          {strValue.length > 30 ? strValue.slice(0, 30) + "..." : strValue}
        </span>
      );
    }

    return (
      <span className="cell-text">
        {strValue.length > 30 ? strValue.slice(0, 30) + "..." : strValue}
      </span>
    );
  };

  // Calculate metrics
  const calculateMetrics = () => {
    if (!data || data.length === 0) {
      return {
        totalRequests: 0,
        avgResponseTime: "0ms",
        successRate: "0%",
        errorRate: "0%"
      };
    }

    const totalRequests = data.length;
    const successCount = data.filter(row => {
      const msg = (row.message ?? "").toString().toLowerCase();
      const st = (row.status ?? "").toString().toLowerCase();
      return msg.includes("success") || st.includes("success") || st === "true" || row.status === true || (typeof row.status === 'number' && row.status >= 200 && row.status < 300);
    }).length;
    const errorCount = data.filter(row => {
      const msg = (row.message ?? "").toString().toLowerCase();
      const st = (row.status ?? "").toString().toLowerCase();
      return msg.includes("error") || msg.includes("fail") || st.includes("error") || st.includes("fail") || st === "false" || row.status === false || (typeof row.status === 'number' && (row.status >= 400 || row.status < 0));
    }).length;

    const latencyValues = data
      .map(row => {
        const raw = row.latency ?? row.response_time ?? row.serve_time;
        const secs = normalizeLatencyMicrosecondsToSeconds(raw);
        if (secs == null) return null;
        return secs * 1000;
      })
      .filter(val => val !== null && !isNaN(val));

    const avgLatency = latencyValues.length > 0
      ? Math.round(latencyValues.reduce((sum, val) => sum + val, 0) / latencyValues.length)
      : 0;

    const successRate = totalRequests > 0 ? ((successCount / totalRequests) * 100).toFixed(1) : 0;
    const errorRate = totalRequests > 0 ? ((errorCount / totalRequests) * 100).toFixed(1) : 0;

    return {
      totalRequests: totalRequests.toLocaleString(),
      avgResponseTime: formatDurationMs(avgLatency),
      successRate: `${successRate}%`,
      errorRate: `${errorRate}%`
    };
  };

  const metrics = calculateMetrics();

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon inactive">⇅</span>;
    }
    return sortConfig.direction === "asc"
      ? <span className="sort-icon active">↑</span>
      : <span className="sort-icon active">↓</span>;
  };

  return (
    <div className="table-wrapper">
      <div className="table-main" ref={tableMainRef} style={{ position: 'relative' }}>
        {filterConfig.showFilters && (
          <div className="filter-overlay">
            <div className="filter-overlay-backdrop" onClick={handleFilterToggle}></div>
            <div className="filter-panel-overlay">
              <div className="filter-panel-header">
                <h4>Filter by Column</h4>
                <button className="close-filter-btn" onClick={handleFilterToggle}>
                  ✕
                </button>
              </div>
              <div className="filter-inputs">
                {columns.map((column) => {
                  // Get unique values for this column to create options
                  const uniqueValues = [...new Set(displayData.map(row => row[column]).filter(val => val !== null && val !== undefined))].slice(0, 10);

                  return (
                    <div key={column} className="filter-input-group">
                      <label htmlFor={`filter-${column}`}>{column}</label>
                      <div className="filter-input-container">
                        <input
                          id={`filter-${column}`}
                          type="text"
                          placeholder={`Filter by ${column}...`}
                          value={filterConfig.filters[column] || ""}
                          onChange={(e) => handleColumnFilter(column, e.target.value)}
                          className="filter-input"
                          list={`${column}-options`}
                        />
                        {uniqueValues.length > 0 && (
                          <datalist id={`${column}-options`}>
                            {uniqueValues.map((value, index) => (
                              <option key={index} value={value} />
                            ))}
                          </datalist>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="filter-panel-footer">
                <button
                  type="button"
                  className="filter-btn filter-btn-secondary"
                  onClick={clearAllFilters}
                >
                  Clear All
                </button>

                <button
                  type="button"
                  className="filter-btn filter-btn-primary"
                  onClick={handleFilterToggle}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Table */}
        <div className="tableContainers analysis-table-wrapper" ref={containersRef}>
          <table className="table-cont analysis-table" ref={tableRef}>
            <thead className={`tableHead ${isScrolled ? "scrolled" : ""}`}>
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={col}
                    style={{ width: uniformColumnWidth }}
                    onClick={() => handleSort(col)}
                    className={`sortable-header ${idx < 2 ? "mobile-visible" : ""}`}
                  >
                    <div className="th-content" style={{ justifyContent: (col.toLowerCase() === "message" || col.toLowerCase() === "status") ? "flex-start" : "space-between" }}>
                      {isMobile && idx === 1 ? (
                        <>
                          <div className="sort-picker-container">
                            <SortIcon columnKey={col} />
                            <span>{col}</span>
                          </div>
                          <div className="column-picker-wrapper">
                            <button
                              className="expand-btn"
                              ref={expandBtnRef}
                              onClick={(e) => {
                                e.stopPropagation();
                                const nextState = !isHeaderDropdownOpen;
                                setIsHeaderDropdownOpen(nextState);
                                if (nextState) {
                                  // Sync temporary picker state with current active columns when opening
                                  setMobileVisibleColumns(mobileActiveCols);
                                  // Compute position relative to .table-main (not viewport)
                                  if (expandBtnRef.current && tableMainRef.current) {
                                    const btnRect = expandBtnRef.current.getBoundingClientRect();
                                    const mainRect = tableMainRef.current.getBoundingClientRect();
                                    setDropdownPos({
                                      top: btnRect.bottom - mainRect.top + 4,
                                      left: Math.max(4, btnRect.left - mainRect.left - 130),
                                      width: 150,
                                    });
                                  }
                                }
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M7 17l-5-5 5-5M17 7l5 5-5 5M2 12h20" />
                              </svg>
                            </button>
                            {/* Dropdown is rendered outside tableContainers — see below table-main */}
                          </div>
                        </>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-start" }}>
                          <span>{col}</span>
                          <SortIcon columnKey={col} />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
                {!isMobile && <th style={{ width: "120px" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((row, idx) => {
                const rowId = indexOfFirstRow + idx;
                const isExpanded = expandedRow === rowId;
                const isSelected = selectedRows.includes(rowId);
                return (
                  <React.Fragment key={rowId}>
                    <tr className={`data-row ${isSelected ? "selected" : ""} ${isExpanded ? "expanded" : ""}`}>
                      {columns.map((col, cIdx) => {
                        const cellValue = row[col];
                        const latencyClass = getLatencyColor(col, cellValue);

                        return (
                          <td
                            key={col}
                            className={`ellipsis-cell ${latencyClass} ${cIdx < 2 ? "mobile-visible" : ""}`}
                            style={{
                              width: uniformColumnWidth,
                              minWidth: "60px",
                              maxWidth: "400px",
                              textAlign: (col.toLowerCase() === "message" || col.toLowerCase() === "status") ? "center" : "left"
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: (col.toLowerCase() === "message" || col.toLowerCase() === "status") ? "center" : "space-between", overflow: "hidden" }}>
                              <span style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                                textAlign: (col.toLowerCase() === "message" || col.toLowerCase() === "status") ? "center" : "left"
                              }}>
                                {renderCell(col, cellValue, row)}
                              </span>
                              {isMobile && cIdx === 1 && (
                                <button
                                  className="mobile-detail-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPopupData(row);
                                    setShowDetailPopup(true);
                                  }}
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    width="14"
                                    height="14"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      {!isMobile && (
                        <td className="actions-cell analysis-actions">
                          <button
                            className="action-btn"
                            onClick={() => {
                              setPopupData(row);
                              setShowDetailPopup(true);
                            }}
                            title="View details"
                          >
                            👁️
                          </button>
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Column picker overlay — rendered here, OUTSIDE tableContainers so overflow:auto doesn't clip it */}
        {isMobile && isHeaderDropdownOpen && (
          <div
            className="column-picker-inline-dropdown"
            style={{
              position: 'absolute',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: 150,
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="picker-inline-header">
              <span>Choose columns</span>
              <button className="close-btn" onClick={() => setIsHeaderDropdownOpen(false)}>✕</button>
            </div>
            <div className="picker-inline-list">
              {allColumns.map(column => {
                const isSelected = mobileVisibleColumns.includes(column);
                const isDisabled = !isSelected && mobileVisibleColumns.length >= 2;
                return (
                  <label
                    key={column}
                    className={`picker-inline-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={(e) => { e.stopPropagation(); toggleColumnSelection(column); }}
                      id={`col-picker-main-${column}`}
                    />
                    {column}
                  </label>
                );
              })}
            </div>
            <div className="picker-inline-footer">
              <button
                className="apply-btn"
                onClick={() => { setMobileActiveCols(mobileVisibleColumns); setIsHeaderDropdownOpen(false); }}
              >
                Apply
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Pagination - Mobile only, outside scroll container so footer search bar won't hide it */}
      {isMobile && displayData.length > 0 && (
        <div className="table-footer pagination mobile-pagination-bar">
          <span className="pagination-info">
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, displayData.length)} of {displayData.length} entries
          </span>
          <div className="pagination-controls pagination mobile-arrows">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
      )}

      {/* Desktop Pagination (Outside Card): Info + Controls on same line */}
      {!isMobile && displayData.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0 0 2px", paddingRight: isHistoryOpen ? "12%" : "0", transition: "padding-right 0.35s ease" }}>
          <span className="pagination-info">
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, displayData.length)} of {displayData.length} entries
          </span>
          <div className="pagination-controls pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {paginationItems.map((item) => {
              if (typeof item === "string") {
                return (
                  <span key={item} className="pagination-ellipsis">
                    ...
                  </span>
                );
              }

              const pageNum = item;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={currentPage === pageNum ? "active" : ""}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Popup for Mobile */}
      {showDetailPopup && popupData && (
        <div className="detail-popup-overlay" onClick={() => setShowDetailPopup(false)}>
          <div className="detail-popup-content compact-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3 style={{ fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#111827' }}>Detailed Information</h3>
              <button
                className="close-popup-btn"
                onClick={() => setShowDetailPopup(false)}
                style={{
                  transition: 'all 0.2s ease',
                  padding: '4px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
            <div className="popup-body">
              <div className="details-vertical-list">
                {allColumns.map((col) => {
                  let val = popupData[col];
                  const colLower = col.toLowerCase();
                  if (colLower === "subject") {
                    val = getSubjectDisplay(val, popupData);
                  } else if (colLower === "connected_from") {
                    val = intToIPv4(val);
                  } else if (colLower.includes("timestamp")) {
                    val = formatTimestamp(val);
                  } else if (colLower === "latency" || colLower === "response_time") {
                    const secs = normalizeLatencyMicrosecondsToSeconds(val);
                    val = secs != null ? `${secs.toFixed(3)}s` : val;
                  } else if (colLower === "location" || colLower.includes("location")) {
                    if (val && typeof val === 'object') {
                      val = getLocationDisplay(val);
                    }
                  }

                  // Try to parse JSON strings for pretty display
                  let processedVal = val;
                  if (typeof processedVal === 'string' && processedVal.trim().startsWith('{')) {
                    try {
                      processedVal = JSON.parse(processedVal);
                    } catch (e) {
                      processedVal = val;
                    }
                  }

                  const isObjectVal = processedVal != null && typeof processedVal === 'object';
                  const isEmpty = processedVal == null || processedVal === '' || processedVal === 'null' || (isObjectVal && Object.keys(processedVal).length === 0);

                  if (isEmpty) return null;

                  return (
                    <div key={col} className={`detail-list-item ${isObjectVal ? 'stack' : ''}`}>
                      <span className="detail-list-label">{col.replace(/_/g, ' ')}:</span>
                      {isObjectVal ? (
                        <div className="detail-list-pre detail-list-pretty-json">
                          <span className="json-brace">{'{'}</span>
                          <div className="json-body">
                            {Object.entries(processedVal).map(([k, v], idx, arr) => (
                              <div key={k} className="json-row">
                                <span className="json-key">{k.replace(/_/g, ' ')}</span>
                                <span className="json-sep">:</span>
                                <span className="json-val">
                                  {v != null && typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                </span>
                                {idx < arr.length - 1 && <span className="json-comma">,</span>}
                              </div>
                            ))}
                          </div>
                          <span className="json-brace">{'}'}</span>
                        </div>
                      ) : (
                        <span className="detail-list-value">{processedVal.toString()}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {tooltip.visible && (
        <div className="cell-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
}

export default TableViews;
